// Mixin to encapsulate background export job logic shared across components
// Provides an `exporting` state flag and a helper method to create a job and download its result.
export default {
    data() {
        return {
            exporting: false
        };
    },

    methods: {
        /**
         * Creates a background export job and downloads the resulting file when finished.
         * Handles the exporting flag, success debug logging, user notification on error, and cleanup.
         *
         * @param {object} opts options
         * @param {string} opts.eventId event id
         * @param {string} opts.jobAppScript appscripts path for the job
         * @param {object} [opts.jobParams] parameters passed to the job appscript
         * @param {object} [opts.jobDocParams] background job document params (name, fileName, etc.)
         * @param {number} [opts.pollEvery=5000] polling interval in ms
         * @param {number} [opts.timeout=2] timeout in minutes
         * @param {string} [opts.filePathKey='filePath'] key name containing the file path in the job result
         * @returns {Promise<string|undefined>} resolves with the downloaded file path or undefined if early-aborted
         */
        async createAndDownloadExport({ eventId, jobAppScript, jobParams = {}, jobDocParams = {}, pollEvery = 5000, timeout = 2, filePathKey = 'filePath' }) {
            try {
                if (this.exporting) return;
                this.exporting = true;

                const result = await this.$services.appScripts.createJobAndWaitForCompletion(eventId, jobAppScript, jobParams, jobDocParams, pollEvery, timeout);
                const filePath = result?.[filePathKey];

                console.debug(`[${this.$options.name}] Export job completed`);
                if (filePath) {
                    this.$utils.browser.downloadFromUrl(filePath);
                } else {
                    console.warn(`[${this.$options.name}] Export job result missing '${filePathKey}'`);
                    this.errorNotify();
                }
                return filePath;
            } catch (error) {
                console.error(`[${this.$options.name}] Could not start export:`, error);
                this.errorNotify();
                throw error;
            } finally {
                this.exporting = false;
            }
        },

        errorNotify() {
            this.$notify({
                group: 'flashes',
                title: this.$i18n.t('general.error_occurred').toString(),
                text: this.$i18n.t('general.try_later').toString(),
                type: 'error',
                duration: 5000
            });
        }
    }
};
